import type { ReactNode } from 'react';
import { Search, X } from 'lucide-react';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import { Input } from '../../../../foundation/altzero_base/base/components/ui/input';
import { FilterDropdown } from './FilterDropdown';
import { SITE_FILTERS, type SiteFilterKey } from '../../lib/siteFilters';
import { monthLabel } from '../../lib/billMonths';
import { WORKFLOW_LABELS, WORKFLOW_ORDER } from '../../lib/billWorkflow';
import { SEND_STATUS_LABELS } from '../../lib/reportStatus';
import {
  ACTIVE_VERDICTS, SEND_STATUS_ORDER, VERDICT_LABELS, hasActiveBillFilters,
  type BillFilterOptions, type BillFilters,
} from '../../lib/billFilters';
import type { BillStatus } from '../../utils/billStatus';

// The filters every bill list shares (spec 2026-10-19): one bar, so Bills, Bulk
// Bill Run and Bulk Customer Report cannot drift apart again. Dropdowns show
// labels; the model (and the URL) hold keys. Screen-specific filters go in
// `children`, after the shared ones and before Clear filters.

type BillFilterBarProps = {
  filters: BillFilters;
  onChange: (patch: Partial<BillFilters>) => void;
  onClear: () => void;
  options: BillFilterOptions;
  /** Keeps each screen's test ids: `${testIdPrefix}-search`, `${testIdPrefix}-clear-filters`. */
  testIdPrefix: string;
  searchPlaceholder?: string;
  /** The Bills list adds Archived; the other lists load active bills only. */
  verdictOptions?: BillStatus[];
  /** A screen-specific filter in `children` is set: Clear filters shows for it too. */
  extraActive?: boolean;
  /** Dropdowns a screen does not use; Clear filters ignores them too. */
  hide?: ReadonlyArray<'verdict' | 'status' | 'send'>;
  /** The From/To placeholders, when the screen has a default month to show. */
  monthAllText?: { from: string; to: string };
  children?: ReactNode;
};

/** The key whose label is `label`; '' (all) for anything else. */
function keyOfLabel<K extends string>(keys: readonly K[], labels: Record<K, string>, label: string): K | '' {
  return keys.find((k) => labels[k] === label) ?? '';
}

function sitePatch(key: SiteFilterKey, value: string): Partial<BillFilters> {
  const patch: Partial<BillFilters> = {};
  patch[key] = value;
  return patch;
}

export const BillFilterBar = ({
  filters, onChange, onClear, options, testIdPrefix, searchPlaceholder = 'Search NMI or client…',
  verdictOptions = ACTIVE_VERDICTS, extraActive = false, hide = [], monthAllText, children,
}: BillFilterBarProps) => {
  const monthLabels = options.months.map((o) => o.label);
  const monthKeyOf = (label: string) => options.months.find((o) => o.label === label)?.key ?? '';
  // A hidden filter that is set (e.g. ?status= from another list) is not shown, so it must not keep Clear filters up.
  const showClear = hasActiveBillFilters({
    ...filters,
    verdict: hide.includes('verdict') ? '' : filters.verdict,
    status: hide.includes('status') ? '' : filters.status,
    send: hide.includes('send') ? '' : filters.send,
  }) || extraActive;

  return (
    <div data-testid="bill-filter-bar" className="flex flex-wrap items-center gap-2">
      <div className="relative w-64">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
        <Input
          data-testid={`${testIdPrefix}-search`}
          aria-label="Search NMI or client"
          placeholder={searchPlaceholder}
          value={filters.q}
          onChange={(e) => onChange({ q: e.target.value })}
          className="pl-8 h-9"
        />
      </div>
      {/* Billing months: pick the same month in both for one month. A bill
          counts for every month its period touches (lib/billMonths). */}
      <FilterDropdown
        testId="bill-filter-from" label="From month" allText={monthAllText?.from ?? 'From: any month'} searchable
        value={filters.from ? monthLabel(filters.from) : ''}
        onChange={(label) => onChange({ from: monthKeyOf(label) })}
        options={monthLabels}
      />
      <FilterDropdown
        testId="bill-filter-to" label="To month" allText={monthAllText?.to ?? 'To: any month'} searchable
        value={filters.to ? monthLabel(filters.to) : ''}
        onChange={(label) => onChange({ to: monthKeyOf(label) })}
        options={monthLabels}
      />
      {SITE_FILTERS.map(({ key, label }) => (
        <FilterDropdown
          key={key}
          testId={`bill-filter-${key}`}
          label={label}
          value={filters[key]}
          onChange={(value) => onChange(sitePatch(key, value))}
          options={options.site[key]}
          searchable={key === 'entity'}
        />
      ))}
      {!hide.includes('verdict') && (
        <FilterDropdown
          testId="bill-filter-verdict"
          label="Verdict"
          value={filters.verdict ? VERDICT_LABELS[filters.verdict] : ''}
          onChange={(label) => onChange({ verdict: keyOfLabel(verdictOptions, VERDICT_LABELS, label) })}
          options={verdictOptions.map((v) => VERDICT_LABELS[v])}
        />
      )}
      {!hide.includes('status') && (
        <FilterDropdown
          testId="bill-filter-status"
          label="Status"
          value={filters.status ? WORKFLOW_LABELS[filters.status] : ''}
          onChange={(label) => onChange({ status: keyOfLabel(WORKFLOW_ORDER, WORKFLOW_LABELS, label) })}
          options={WORKFLOW_ORDER.map((s) => WORKFLOW_LABELS[s])}
        />
      )}
      {!hide.includes('send') && (
        <FilterDropdown
          testId="bill-filter-send" label="Send status" allText="All send statuses"
          value={filters.send ? SEND_STATUS_LABELS[filters.send] : ''}
          onChange={(label) => onChange({ send: keyOfLabel(SEND_STATUS_ORDER, SEND_STATUS_LABELS, label) })}
          options={SEND_STATUS_ORDER.map((s) => SEND_STATUS_LABELS[s])}
        />
      )}
      {children}
      {showClear && (
        <Button
          data-testid={`${testIdPrefix}-clear-filters`}
          variant="ghost"
          size="sm"
          className="h-9 text-xs text-gray-500 gap-1"
          onClick={onClear}
        >
          <X className="h-3.5 w-3.5" />
          Clear filters
        </Button>
      )}
    </div>
  );
};
